import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import api, { esSrc } from "@/lib/api";
import { enableSound, disableSound, isSoundEnabled, blip, NOTES } from "@/lib/sound";
import { toast } from "sonner";
import { ArrowRight, Sparkles, Eye as EyeIcon, Volume2, VolumeX, Send, ArrowUpRight, Plus, Minus, ExternalLink, Menu, X, Instagram, Facebook, Youtube, Music2, ShoppingBag, Twitter, Mail, Globe } from "lucide-react";

const A = "/assets/es/";

function CursorTrail() {
  const ref = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(hover: none)").matches) return;
    const canvas = ref.current, ctx = canvas.getContext("2d");
    let w = (canvas.width = window.innerWidth), h = (canvas.height = window.innerHeight);
    const onResize = () => { w = canvas.width = window.innerWidth; h = canvas.height = window.innerHeight; };
    window.addEventListener("resize", onResize);
    const colors = ["#FF2E9A", "#21E6D3", "#FFE14D", "#FFFFFF"];
    let ps = [];
    const onMove = (e) => {
      for (let i = 0; i < 2; i++) ps.push({ x: e.clientX + (Math.random() - .5) * 8, y: e.clientY + (Math.random() - .5) * 8, vx: (Math.random() - .5) * 1.2, vy: (Math.random() - .5) * 1.2 + .4, life: 1, size: Math.random() * 2.4 + 1, color: colors[(Math.random() * colors.length) | 0] });
      if (ps.length > 220) ps = ps.slice(-220);
    };
    window.addEventListener("mousemove", onMove);
    let raf;
    const loop = () => {
      ctx.clearRect(0, 0, w, h);
      for (const p of ps) { p.x += p.vx; p.y += p.vy; p.life -= .028; if (p.life <= 0) continue; ctx.globalAlpha = Math.max(p.life, 0); ctx.fillStyle = p.color; ctx.shadowBlur = 10; ctx.shadowColor = p.color; ctx.beginPath(); ctx.arc(p.x, p.y, p.size * p.life, 0, Math.PI * 2); ctx.fill(); }
      ctx.globalAlpha = 1; ctx.shadowBlur = 0; ps = ps.filter((p) => p.life > 0); raf = requestAnimationFrame(loop);
    };
    loop();
    return () => { cancelAnimationFrame(raf); window.removeEventListener("mousemove", onMove); window.removeEventListener("resize", onResize); };
  }, []);
  return <canvas ref={ref} className="es-trail" style={{ position: "fixed", inset: 0, width: "100%", height: "100%", zIndex: 10 }} />;
}

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-70px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }} className={className}>{children}</motion.div>
);

const embedUrl = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return null;
};

function FlipCard({ img, name, meaning, note, onHover }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div className={`es-flip group h-[420px] cursor-pointer ${flipped ? "is-flipped" : ""}`}
      onClick={() => setFlipped((f) => !f)} onMouseEnter={() => onHover && onHover(note)}>
      <div className="es-flip-inner relative w-full h-full">
        <div className="es-flip-face absolute inset-0 rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent flex flex-col items-center justify-center px-6 overflow-hidden">
          <div className="h-52 flex items-center justify-center">
            <img src={esSrc(img)} alt={name} className="max-h-52 max-w-full object-contain drop-shadow-[0_0_28px_rgba(33,230,211,0.35)]" />
          </div>
          <h3 className="es-gothic text-3xl mt-8 es-glow-turq text-center">{name}</h3>
          <span className="mt-3 text-[11px] uppercase tracking-[0.35em] text-white/40">tap to reveal</span>
        </div>
        <div className="es-flip-face es-flip-back absolute inset-0 rounded-3xl border border-[var(--es-pink)]/40 bg-[#141014] flex flex-col items-center justify-center px-8 text-center">
          <h3 className="es-gothic text-2xl es-glow-pink mb-5">{name}</h3>
          <p className="es-body text-white/75 text-base leading-relaxed">{meaning}</p>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Block renderers ---------------- */
function Hero({ d, onHover }) {
  const video = embedUrl(d.video_url);
  return (
    <section className="relative min-h-[92vh] flex items-center pt-28 pb-20 overflow-hidden" data-testid="public-hero-section">
      <img src={`${A}planet-pink.webp`} alt="" className="pointer-events-none absolute es-float w-40 md:w-56 top-24 right-[6%] opacity-80" style={{ animationDuration: "9s" }} />
      <img src={`${A}planet-turq.webp`} alt="" className="pointer-events-none absolute es-float w-24 md:w-32 bottom-24 left-[8%] opacity-70" style={{ animationDuration: "11s" }} />
      <img src={`${A}stars-gold.webp`} alt="" className="pointer-events-none absolute es-twinkle w-20 top-40 left-[14%]" />
      <img src={`${A}bolt.webp`} alt="" className="pointer-events-none absolute es-float w-10 top-1/3 right-[16%]" style={{ animationDuration: "6s" }} />
      <div className="relative z-20 max-w-7xl mx-auto w-full px-6 lg:px-10 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          {d.eyebrow && <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-6">{d.eyebrow}</motion.p>}
          <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }} className="es-gothic text-5xl sm:text-6xl lg:text-7xl es-glow-pink">{d.heading}</motion.h1>
          {d.subheading && <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="es-body text-lg text-white/70 mt-7 max-w-xl">{d.subheading}</motion.p>}
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }} className="flex flex-wrap items-center gap-4 mt-10">
            {d.cta_label && d.cta_url && <a href={d.cta_url} target="_blank" rel="noreferrer" data-testid="hero-cta" className="es-btn-pink px-8 py-4 rounded-full font-bold inline-flex items-center gap-2"><Sparkles className="w-5 h-5" /> {d.cta_label}</a>}
            {d.cta2_label && d.cta2_url && <a href={d.cta2_url} className="es-btn-ghost px-8 py-4 rounded-full font-semibold inline-flex items-center gap-2"><EyeIcon className="w-5 h-5" /> {d.cta2_label}</a>}
          </motion.div>
        </div>
        <div className="relative flex items-center justify-center min-h-[360px]" onMouseEnter={() => onHover && onHover(NOTES.eye)}>
          <div className="absolute w-[340px] h-[340px] md:w-[440px] md:h-[440px] rounded-full bg-[radial-gradient(circle,rgba(255,46,154,0.28),transparent_62%)] blur-xl" />
          {d.image && <motion.img initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} src={esSrc(d.image)} alt={d.heading} className="relative z-10 w-[300px] md:w-[420px] drop-shadow-[0_0_50px_rgba(33,230,211,0.4)]" data-testid="hero-eye" />}
          {video && <div className="absolute z-10 bottom-0 right-0 w-[280px] aspect-video rounded-xl overflow-hidden shadow-2xl border border-white/20"><iframe src={video} title="video" className="w-full h-full" allowFullScreen /></div>}
        </div>
      </div>
    </section>
  );
}

function Manifesto({ d }) {
  const paras = (d.body || "").split(/\n\s*\n/).filter(Boolean);
  return (
    <section className="relative py-28 lg:py-36 overflow-hidden" data-testid="public-manifesto-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
        {d.image && <Reveal><img src={esSrc(d.image)} alt="" className="rounded-2xl border border-white/10 w-full" /></Reveal>}
        <Reveal delay={0.15}>
          {d.eyebrow && <p className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-5">{d.eyebrow}</p>}
          <h2 className="es-gothic text-4xl lg:text-5xl es-glow-turq mb-6">{d.heading}</h2>
          <hr className="es-hr mb-7" />
          {paras.map((p, i) => <p key={i} className="es-body text-white/70 mb-5">{p}</p>)}
          {d.quote && (
            <blockquote className="es-gothic text-2xl leading-snug text-white es-glow-pink border-l-2 border-[var(--es-pink)] pl-5 mt-6">
              “{d.quote}”{d.quote_author && <span className="block es-body text-sm tracking-wide text-white/50 mt-4">— {d.quote_author}</span>}
            </blockquote>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function Symbols({ d, onHover }) {
  const notes = [NOTES.eye, NOTES.heart, NOTES.nazar, NOTES.high, NOTES.soft];
  return (
    <section className="relative py-28 lg:py-36 bg-[#080808] overflow-hidden" data-testid="public-symbols-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <Reveal className="text-center mb-16">
          {d.eyebrow && <p className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-4">{d.eyebrow}</p>}
          {d.heading && <h2 className="es-gothic text-4xl lg:text-6xl es-glow-turq">{d.heading}</h2>}
          {d.subheading && <p className="es-body text-white/55 mt-5 max-w-xl mx-auto">{d.subheading}</p>}
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {(d.items || []).map((it, i) => <FlipCard key={i} img={it.image} name={it.name} meaning={it.meaning} note={notes[i % notes.length]} onHover={onHover} />)}
        </div>
      </div>
    </section>
  );
}

function Gallery({ d }) {
  return (
    <section className="relative py-28 lg:py-32 overflow-hidden" data-testid="public-gallery-section">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <Reveal className="mb-14">
          {d.eyebrow && <p className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-4">{d.eyebrow}</p>}
          {d.heading && <h2 className="es-gothic text-4xl lg:text-5xl es-glow-pink">{d.heading}</h2>}
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
          {(d.items || []).map((it, i) => {
            const inner = (
              <div className="group relative rounded-2xl border border-white/10 bg-white/[0.03] p-6 aspect-square flex items-center justify-center overflow-hidden">
                <img src={esSrc(it.image)} alt={it.caption || ""} className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-110" />
                {it.caption && <span className="absolute bottom-3 left-0 right-0 text-center text-sm text-white/70">{it.caption}</span>}
              </div>
            );
            return <Reveal key={i} delay={(i % 3) * 0.07}>{it.link ? <a href={it.link} target="_blank" rel="noreferrer">{inner}</a> : inner}</Reveal>;
          })}
        </div>
      </div>
    </section>
  );
}

function Collections({ d, onHover, settings }) {
  const notes = [NOTES.eye, NOTES.heart, NOTES.nazar];
  const shopHref = (it) => it.url || settings?.social_etsy || "https://www.etsy.com";
  return (
    <section className="relative py-28 lg:py-36 overflow-hidden" data-testid="public-collections-section">
      <img src={`${A}prism.webp`} alt="" className="pointer-events-none absolute es-float w-72 top-6 left-[2%] opacity-40" style={{ animationDuration: "14s" }} />
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <Reveal className="mb-16 max-w-2xl">
          {d.eyebrow && <p className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-4">{d.eyebrow}</p>}
          {d.heading && <h2 className="es-gothic text-4xl lg:text-6xl es-glow-pink mb-5">{d.heading}</h2>}
          {d.body && <p className="es-body text-white/60">{d.body}</p>}
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6">
          {(d.items || []).map((it, i) => (
            <Reveal key={i} delay={(i % 3) * 0.07} className="group relative rounded-3xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-[var(--es-turq)]/40 transition-colors flex flex-col">
              <div onMouseEnter={() => onHover && onHover(notes[i % notes.length])} className="aspect-square flex items-center justify-center p-10 bg-[radial-gradient(circle_at_50%_40%,rgba(33,230,211,0.08),transparent_60%)]">
                <img src={esSrc(it.image)} alt={it.title} className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-7 border-t border-white/10 flex flex-col flex-1">
                {it.kicker && <p className="text-[11px] uppercase tracking-[0.3em] text-[var(--es-turq)] mb-2">{it.kicker}</p>}
                <h3 className="es-gothic text-2xl mb-3">{it.title}</h3>
                {it.desc && <p className="es-body text-white/60 text-sm leading-relaxed mb-5">{it.desc}</p>}
                <a href={shopHref(it)} target="_blank" rel="noreferrer" data-testid={`collection-shop-${i}`}
                   className="mt-auto es-btn-pink inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold text-sm">
                  Shop on Etsy <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </Reveal>
          ))}
        </div>
        {d.cta_label && d.cta_url && (
          <Reveal className="text-center mt-14">
            <a href={d.cta_url} target="_blank" rel="noreferrer" className="es-btn-ghost inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold">{d.cta_label} <ArrowRight className="w-5 h-5" /></a>
          </Reveal>
        )}
      </div>
    </section>
  );
}

const PinterestIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M11 13.5 L 8.5 21" />
    <path d="M11 13.5 C 8.4 13 7.4 9.7 9.4 7.5 C 11.4 5.3 15.4 5.6 16.9 8.4 C 18.3 11 16.4 13.7 13 13.4" />
  </svg>
);

const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube, tiktok: Music2, etsy: ShoppingBag, x: Twitter, pinterest: PinterestIcon, email: Mail };
const SOCIAL_LABELS = { instagram: "Instagram", facebook: "Facebook", youtube: "YouTube", tiktok: "TikTok", etsy: "Etsy", x: "X", pinterest: "Pinterest", email: "Email" };

function Socials({ d, settings }) {
  let items = (d.items || []).filter((it) => it.url && it.url.trim());
  if (items.length === 0 && settings) {
    const fromSettings = [
      { platform: "instagram", url: settings.social_instagram },
      { platform: "tiktok", url: settings.social_tiktok },
      { platform: "youtube", url: settings.social_youtube },
      { platform: "facebook", url: settings.social_facebook },
      { platform: "etsy", url: settings.social_etsy },
      { platform: "email", url: settings.contact_email ? `mailto:${settings.contact_email}` : "" },
    ];
    items = fromSettings.filter((it) => it.url && it.url.trim());
  }
  return (
    <section className="relative py-28 overflow-hidden" data-testid="public-socials-section">
      <img src={`${A}sparkle-turq.webp`} alt="" className="pointer-events-none absolute es-twinkle w-12 top-14 right-[12%]" />
      <img src={`${A}star-heart.webp`} alt="" className="pointer-events-none absolute es-float w-14 bottom-10 left-[10%]" style={{ animationDuration: "8s" }} />
      <div className="max-w-4xl mx-auto px-6 text-center">
        <Reveal>
          {d.eyebrow && <p className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-4">{d.eyebrow}</p>}
          {d.heading && <h2 className="es-gothic text-4xl lg:text-6xl es-glow-turq mb-4">{d.heading}</h2>}
          {d.subheading && <p className="es-body text-white/60 max-w-xl mx-auto mb-12">{d.subheading}</p>}
        </Reveal>
        <div className="flex flex-wrap items-start justify-center gap-6 md:gap-10">
          {items.map((it, i) => {
            const Icon = SOCIAL_ICONS[it.platform] || Globe;
            return (
              <Reveal key={i} delay={(i % 6) * 0.06}>
                <a href={it.url} target="_blank" rel="noreferrer" data-testid={`social-${it.platform}-${i}`}
                   className="group flex flex-col items-center gap-3">
                  <span className="w-20 h-20 md:w-24 md:h-24 rounded-full border-2 border-[var(--es-turq)]/60 bg-white/[0.03] flex items-center justify-center text-white transition-all duration-300 group-hover:border-[var(--es-pink)] group-hover:text-[var(--es-pink)] group-hover:-translate-y-1.5 group-hover:shadow-[0_0_34px_rgba(255,46,154,0.6)]">
                    <Icon className="w-9 h-9 md:w-11 md:h-11" strokeWidth={1.6} />
                  </span>
                  <span className="es-body text-sm tracking-widest text-white/60 group-hover:text-white transition-colors">{it.label || SOCIAL_LABELS[it.platform] || ""}</span>
                </a>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function RichText({ d }) {
  const paras = (d.body || "").split(/\n\s*\n/).filter(Boolean);
  return (
    <section className="relative py-24 lg:py-28 overflow-hidden" data-testid="public-richtext-section">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <Reveal>
          {d.eyebrow && <p className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-4">{d.eyebrow}</p>}
          <h2 className="es-gothic text-4xl lg:text-5xl es-glow-turq mb-6">{d.heading}</h2>
          {paras.map((p, i) => <p key={i} className="es-body text-white/65 mb-4">{p}</p>)}
        </Reveal>
      </div>
    </section>
  );
}

function Newsletter({ d }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const submit = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error("Please enter a valid email"); return; }
    setStatus("sending");
    try { const r = await api.post("/public/newsletter", { email }); setStatus("done"); toast.success(r.data.already ? "You're already on the list ✦" : "You're in. Welcome to the signal ✦"); setEmail(""); }
    catch (err) { setStatus("idle"); toast.error(err.response?.data?.detail || "Something went wrong"); }
  };
  return (
    <section className="relative py-24 overflow-hidden" data-testid="public-newsletter-section">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <Reveal>
          {d.eyebrow && <p className="text-[12px] uppercase tracking-[0.4em] text-[var(--es-turq)] mb-4">{d.eyebrow}</p>}
          <h2 className="es-gothic text-4xl lg:text-5xl es-glow-pink mb-5">{d.heading}</h2>
          {d.body && <p className="es-body text-white/60 max-w-xl mx-auto mb-9">{d.body}</p>}
          {status === "done" ? (
            <p data-testid="newsletter-success" className="es-gothic text-2xl es-glow-turq">✦ Signal received. Welcome. ✦</p>
          ) : (
            <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input data-testid="newsletter-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" className="flex-1 bg-white/[0.05] border border-white/15 rounded-full px-6 py-4 text-white placeholder-white/35 outline-none focus:border-[var(--es-turq)] transition-colors" />
              <button data-testid="newsletter-submit" disabled={status === "sending"} className="es-btn-pink px-7 py-4 rounded-full font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">{status === "sending" ? "Sending…" : <>Join <Send className="w-4 h-4" /></>}</button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}

function CTA({ d, settings }) {
  const href = d.cta_url || settings?.social_etsy || "https://www.etsy.com";
  return (
    <section className="relative py-28 bg-[#080808] border-t border-white/10 overflow-hidden" data-testid="public-cta-section">
      <img src={`${A}heart-sun.webp`} alt="" className="pointer-events-none absolute es-float w-28 top-10 right-[10%] opacity-50" style={{ animationDuration: "9s" }} />
      <div className="max-w-3xl mx-auto px-6 text-center">
        <Reveal>
          <h2 className="es-gothic text-4xl lg:text-6xl es-glow-turq mb-6">{d.heading}</h2>
          {d.body && <p className="es-body text-white/65 max-w-2xl mx-auto mb-10">{d.body}</p>}
          {d.cta_label && <a href={href} target="_blank" rel="noreferrer" data-testid="final-cta" className="es-btn-pink inline-flex items-center gap-2 px-9 py-4 rounded-full font-bold text-lg">{d.cta_label} <ArrowRight className="w-5 h-5" /></a>}
        </Reveal>
      </div>
    </section>
  );
}

const MAP = { es_hero: Hero, es_manifesto: Manifesto, es_symbols: Symbols, es_gallery: Gallery, es_collections: Collections, es_socials: Socials, es_richtext: RichText, es_newsletter: Newsletter, es_cta: CTA };

export function ESBlocks({ blocks = [], onHover, settings }) {
  return (
    <>
      {blocks.map((b) => {
        const C = MAP[b.type];
        if (!C) return null;
        return <div id={`sec-${b.id}`} key={b.id} className="scroll-mt-24"><C d={b.data || {}} onHover={onHover} settings={settings} /></div>;
      })}
    </>
  );
}

export function ESShell({ blocks = [], settings, children }) {
  const [scrolled, setScrolled] = useState(false);
  const [soundOn, setSoundOn] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => { const on = () => setScrolled(window.scrollY > 40); window.addEventListener("scroll", on); return () => window.removeEventListener("scroll", on); }, []);
  const title = settings?.site_title || "EmotionalSignal";
  const etsy = settings?.social_etsy;
  const nav = (blocks || []).filter((b) => b.data?.nav_title?.trim()).map((b) => ({ id: b.id, title: b.data.nav_title }));
  const toggleSound = async () => { if (isSoundEnabled()) { disableSound(); setSoundOn(false); } else { await enableSound(); setSoundOn(true); blip(NOTES.high); } };

  return (
    <div className="es-root es-noise es-body min-h-screen relative" data-testid="es-shell">
      <CursorTrail />
      <button onClick={toggleSound} data-testid="sound-toggle" aria-label="Toggle sound"
        className={`fixed z-50 bottom-6 left-6 w-12 h-12 rounded-full flex items-center justify-center border transition-all ${soundOn ? "bg-[var(--es-turq)]/15 border-[var(--es-turq)] text-[var(--es-turq)] shadow-[0_0_24px_rgba(33,230,211,0.5)]" : "bg-white/5 border-white/20 text-white/60 hover:text-white"}`}>
        {soundOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
      </button>

      <header data-testid="es-header" className={`fixed top-0 inset-x-0 z-40 transition-[background-color,backdrop-filter,padding] duration-300 ${scrolled || mobileOpen ? "bg-[#0B0B0B]/90 backdrop-blur-xl border-b border-white/10 py-4" : "py-6"}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between gap-4">
          <a href="#top" className="es-gothic text-2xl es-glow-turq shrink-0" onClick={() => setMobileOpen(false)}>{title}</a>
          <nav className="hidden md:flex items-center gap-7 text-sm text-white/70">
            {nav.map((n) => <a key={n.id} href={`#sec-${n.id}`} data-testid={`nav-${n.id}`} className="hover:text-[var(--es-turq)] transition-colors">{n.title}</a>)}
            {etsy && <a href={etsy} target="_blank" rel="noreferrer" data-testid="header-etsy" className="es-btn-pink px-5 py-2 rounded-full font-semibold">Shop on Etsy</a>}
          </nav>
          {(nav.length > 0 || etsy) && (
            <button className="md:hidden text-white p-2" data-testid="mobile-menu-btn" aria-label="Menu" onClick={() => setMobileOpen((o) => !o)}>
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          )}
        </div>
        {mobileOpen && (
          <div className="md:hidden px-6 pt-4 pb-6 flex flex-col gap-4" data-testid="mobile-nav">
            {nav.map((n) => <a key={n.id} href={`#sec-${n.id}`} data-testid={`mnav-${n.id}`} onClick={() => setMobileOpen(false)} className="text-white/80 text-lg hover:text-[var(--es-turq)] transition-colors">{n.title}</a>)}
            {etsy && <a href={etsy} target="_blank" rel="noreferrer" onClick={() => setMobileOpen(false)} className="es-btn-pink px-5 py-3 rounded-full font-semibold text-center mt-2">Shop on Etsy</a>}
          </div>
        )}
      </header>

      <div id="top" />
      <main className="relative z-10">{children}</main>

      <footer className="relative z-10 py-10 text-center border-t border-white/10">
        <p className="es-gothic text-xl es-glow-pink">Created with intention & emotion</p>
        <p className="es-body text-white/40 text-sm mt-2">© {new Date().getFullYear()} {title}</p>
      </footer>
    </div>
  );
}
